// Tipos para tratamento padronizado de erros da API
export interface ErrorDetails {
  statusCode: number;
  property?: string;
  message: string;
}

export interface ApiErrorResponse {
  errors?: ErrorDetails[];
  message?: string;
  statusCode?: number;
}

// Enum para tipos de erro HTTP
export enum HttpErrorType {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

// Classe utilitária para processar erros
export class ErrorHandler {
  /**
   * Sanitiza mensagens de erro para não expor informações sensíveis
   */
  private static sanitizeErrorMessage(message: string): string {
    // Remove informações técnicas sensíveis
    const sanitized = message
      .replace(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, '[IP]') // IPs
      .replace(/[a-f0-9]{32}/gi, '[HASH]') // Hashes
      .replace(/Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi, '[TOKEN]') // JWT tokens
      .replace(/password[=:]\s*\S+/gi, 'password=[REDACTED]') // Senhas em URLs
      .replace(/api[_-]?key[=:]\s*\S+/gi, 'api_key=[REDACTED]') // API keys
      .replace(/\/home\/[\w-]+/g, '/home/[USER]') // Paths Unix
      .replace(/C:\\Users\\[\w-]+/g, 'C:\\Users\\[USER]') // Paths Windows
      .replace(/at\s+[\w.]+\s+\([^)]+:\d+:\d+\)/g, '[STACK_TRACE]'); // Stack traces

    return sanitized;
  }

  /**
   * Retorna mensagem específica baseada no código HTTP
   */
  private static getHttpErrorMessage(statusCode: number, originalMessage?: string): string {
    const sanitizedMessage = originalMessage ? this.sanitizeErrorMessage(originalMessage) : undefined;

    switch (statusCode) {
      case HttpErrorType.UNAUTHORIZED:
        return 'E-mail ou senha incorretos. Verifique suas credenciais e tente novamente.';

      case HttpErrorType.FORBIDDEN:
        return 'Acesso negado. Você não tem permissão para esta operação.';

      case HttpErrorType.NOT_FOUND:
        return sanitizedMessage?.includes('URL') || sanitizedMessage?.includes('endpoint')
          ? 'Recurso não encontrado. Verifique se a URL está correta.'
          : 'O item solicitado não foi encontrado no sistema.';

      case HttpErrorType.CONFLICT:
        return sanitizedMessage || 'Já existe um registro com essas informações.';

      case HttpErrorType.UNPROCESSABLE_ENTITY:
        return sanitizedMessage || 'Os dados enviados não puderam ser processados.';

      case HttpErrorType.INTERNAL_SERVER_ERROR:
        return 'Erro interno do servidor. Tente novamente em alguns instantes.';

      case HttpErrorType.BAD_GATEWAY:
        return 'Erro de comunicação com o servidor. Tente novamente.';

      case HttpErrorType.SERVICE_UNAVAILABLE:
        return 'Serviço temporariamente indisponível. Tente novamente mais tarde.';

      case HttpErrorType.GATEWAY_TIMEOUT:
        return 'Tempo limite de resposta excedido. Verifique sua conexão e tente novamente.';

      default:
        if (statusCode >= 500) {
          return 'Erro interno do servidor. Nossa equipe foi notificada.';
        }
        if (statusCode >= 400) {
          return originalMessage || 'Erro na requisição. Verifique os dados enviados.';
        }
        return originalMessage || 'Erro desconhecido ocorreu.';
    }
  }

  /**
   * Processa resposta de erro da API e retorna mensagens formatadas
   */
  static processApiError(error: any): string[] {
    if (!error) return ['Erro desconhecido'];

    // Verifica se é erro de conexão/rede
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      return ['Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.'];
    }

    // Verifica outros tipos de erro de rede
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      return ['Erro de conexão. Verifique sua conexão com a internet.'];
    }

    // Verifica erro de rede (NetworkError)
    if (error.name === 'NetworkError' || error.message?.includes('NetworkError')) {
      return ['Erro de rede. Verifique sua conexão com a internet.'];
    }

    // Verifica timeout
    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      return ['Tempo limite excedido. Tente novamente em alguns instantes.'];
    }

    // Trata erros HTTP específicos
    if (error.statusCode) {
      const httpMessage = this.getHttpErrorMessage(error.statusCode, error.message);
      return [httpMessage];
    }

    // Se já é uma resposta processada
    if (error.errors && Array.isArray(error.errors)) {
      return error.errors.map((err: ErrorDetails) => {
        if (err.property) {
          return `${err.property}: ${err.message}`;
        }
        return err.message;
      });
    }

    // Se é um erro HTTP padrão
    if (error.response?.data) {
      const data = error.response.data;

      // Verifica se tem estrutura ErrorDetails
      if (data.errors && Array.isArray(data.errors)) {
        return data.errors.map((err: ErrorDetails) => {
          if (err.property) {
            return `${err.property}: ${err.message}`;
          }
          return err.message;
        });
      }

      // Fallback para mensagens padrão
      if (data.message) return [data.message];
      if (data.error) return [data.error];
    }

    // Fallback para erro genérico
    if (error.message) return [error.message];

    return ['Erro interno do servidor'];
  }

  /**
   * Retorna o tipo de erro baseado no código HTTP
   */
  static getHttpErrorType(statusCode: number): HttpErrorType | null {
    return Object.values(HttpErrorType).includes(statusCode as HttpErrorType)
      ? (statusCode as HttpErrorType)
      : null;
  }

  /**
   * Verifica se o erro é de um tipo específico
   */
  static isHttpError(error: any, errorType: HttpErrorType): boolean {
    return error?.statusCode === errorType;
  }

  /**
   * Verifica se é erro de "não encontrado"
   */
  static isNotFoundError(error: any): boolean {
    return this.isHttpError(error, HttpErrorType.NOT_FOUND);
  }

  /**
   * Verifica se é erro de validação
   */
  static isValidationError(error: any): boolean {
    return error?.statusCode === HttpErrorType.BAD_REQUEST ||
           error?.statusCode === HttpErrorType.UNPROCESSABLE_ENTITY;
  }

  /**
   * Verifica se é erro de autenticação/autorização
   */
  static isAuthError(error: any): boolean {
    return error?.statusCode === HttpErrorType.UNAUTHORIZED ||
           error?.statusCode === HttpErrorType.FORBIDDEN;
  }
  static getFieldErrors(error: any, property: string): string[] {
    if (!error) return [];

    const errors = this.processApiError(error);
    return errors.filter(err => err.toLowerCase().startsWith(`${property.toLowerCase()}:`))
                 .map(err => err.substring(property.length + 2)); // Remove "property: "
  }

  /**
   * Verifica se um erro específico existe para uma propriedade
   */
  static hasFieldError(error: any, property: string): boolean {
    return this.getFieldErrors(error, property).length > 0;
  }

  /**
   * Obtém mensagem de erro geral (não específica de campo)
   */
  static getGeneralError(error: any): string | null {
    if (!error) return null;

    const errors = this.processApiError(error);
    return errors.find(err => !err.includes(':')) || errors[0] || null;
  }
}

// Hook personalizado para tratamento de erros
export const useErrorHandler = () => {
  return {
    processApiError: ErrorHandler.processApiError,
    getFieldErrors: ErrorHandler.getFieldErrors,
    hasFieldError: ErrorHandler.hasFieldError,
    getGeneralError: ErrorHandler.getGeneralError,
  };
};